import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface NerNorRequest {
    content: string;
    structure_enabled: boolean;
}

export type EntityCardType =
    | 'drug'
    | 'target'
    | 'organization'
    | 'disease'
    | 'news'
    | 'patent'
    | 'paper'
    | 'clinical_result'
    | 'clinical_trials'
    | 'drug_deal'

/**
 * 实体类型枚举
 */
export enum EntityType {
    Drug = 'Drug', // 药物
    Organization = 'Org', // 机构
    Target = 'Target', // 靶点
    Disease = 'Disease', // 适应症
    Patent = 'Patent', // 专利
    Paper = 'Paper', // 专利
    ClinicalTrial = 'Clinical_Trial', // 临床试验
    ClinicalResults = 'Clinical_Results', // 临床试验
    DrugDeal = 'DrugDeal', // 药物合作伙伴
    Structure = 'structure', // 结构
    Mechanism = 'Mechanism' // 机制
}

export interface NerNorResponse {
    data_type: EntityType // 实体类型
    end_index: number // 结束位置下标
    normalized_id: string // 实体唯一标识ID
    original_name: string // 实体原始名称
    start_index: number // 开始位置下标
}

export interface ExtractReferenceLayoutResult {
    bbox: number[]
    page: number
}

export interface PDFOrWebsiteReferenceData {
    attachment_location?: string[]
    data_type?: 'text' | 'table' | 'figure'
    doc_format?: 'pdf' | 'text'
    doc_type?: string
    doc_type_detail?: string
    file_location?: string
    page_location?: ExtractReferenceLayoutResult[]
    section?: string[]
    reference_id: string
    text: string
    title: string
    url?: string
}

export interface EntitySourceInfo {
    code: string
    normalized_source_id: string
    source: string
}

export interface EntityNameInfo {
    name: string
    lang: string
    status: string
    source_info?: EntitySourceInfo[]
}

export interface DevStatusView {
    dict_id: string
    dict_type: string
    display_name_cn: string
    display_name_en: string
    name_cn: string
    name_en: string
    rank: number
    rnk: number
}

export interface RelatedDrugView {
    drug_id: string
    display_name_cn: string
    display_name_en: string
    drug_name: EntityNameInfo[]
    drug_type: string[]
    mechanism_action_id: string[]
    disease_id?: string[]
    target_id?: string[]
    therapeutic_area?: string[]
    target_extend_id?: string
    inactive_org_master_entity_id?: string[]
    data_source: number
    patent_mining: boolean
    mechanism_action_id_count: number
    target_id_count?: number
}

export interface TargetCardData {
    target_id: string
    target_name: EntityNameInfo[]
    short_name: EntityNameInfo[]
    display_name_cn: string
    display_name_en: string
    related_drug_id: string[]
    related_drug_id_count: number
    related_drug_id_view: RelatedDrugView[]
    highest_dev_status: string
    highest_dev_status_view: DevStatusView
}

export interface DiseaseCardData {
    disease_id: string
    display_name_cn: string
    display_name_en: string
    disease_name: EntityNameInfo[]
    alias: EntityNameInfo[]
    related_drug_id: string[]
    related_drug_id_count: number
    related_drug_id_view: RelatedDrugView[]
    highest_dev_status: string
    highest_dev_status_view: DevStatusView
}

export interface CountryView {
    id: string
    region_id: string
    display_name: string
    display_name_en: string
    display_name_cn: string
    alpha_2_code: string
}

export interface DrugCardTargetView {
    target_id: string
    display_name_cn: string
    display_name_en: string
    target_name: { name: string; lang: string }[]
}

export interface MechanismView {
    mechanism_id: string
    display_name_cn: string
    display_name_en: string
    mechanism_name: { name: string; lang: string }[]
    short_name: { name: string; lang: string }[]
}

export interface DrugCardOrgView {
    entity_id: string
    name_en: string
    name_cn_translation?: string
    display_name_cn: string
    display_name_en: string
    description_cn_translation?: string
    website?: string
    founded_date?: number
    employee_number?: number
    country_id?: string
    state_id?: string
    city_id?: string
    entity_status: string
    logo?: string
    data_status: string
    normalized_entity_type_cn_translation?: string
    normalized_entity_type_en?: string
    stock_exchange_code?: string
    stock_symbol?: string
    went_public_date?: number
    parent_entity_id?: string
    parent_ids?: string[]
    ultimate_parent_entity_id?: string
    max_percent?: number
    ownership_type?: string[]
    ultimate_parent_id?: string[]
    drug_count?: number
    drug_count_roll_up?: number
    patent_phs_count?: number
    patent_phs_count_roll_up?: number
    is_ls_related?: boolean
    dev_drug_count?: number
    dev_drug_count_roll_up?: number
    profile_en?: string
    start_up?: boolean
    extinct?: boolean
    is_corp_tree?: boolean
    is_china?: boolean
    is_factset?: boolean
}

export interface DiseaseProfileInfo {
    content: string
    lang: string
    if_manual: boolean
    display_priority: boolean
    status: string
    source_info?: EntitySourceInfo[]
}

export interface DrugCardDiseaseView {
    disease_id: string
    display_name_cn: string
    display_name_en: string
    disease_name: EntityNameInfo[]
    ancestor_id?: string[]
    therapeutic_area?: string[]
    profile?: DiseaseProfileInfo[]
    profile_v2?: DiseaseProfileInfo[]
    dev_drug_count?: number
    dev_drug_count_roll_up?: number
    umls_cui?: string[]
    is_leaf?: boolean
}

export interface DrugCardData {
    drug_id: string
    display_name_cn: string
    display_name_en: string
    drug_name: EntityNameInfo[]
    first_approved_date?: number
    first_approved_country?: string[]
    first_approved_country_view?: CountryView[]
    target_id?: string[]
    single_target_ids?: string[]
    target_id_count?: number
    target_id_view?: DrugCardTargetView[]
    mechanism_action_id?: string[]
    mechanism_action_id_count?: number
    mechanism_action_id_view?: MechanismView[]
    active_org_master_entity_id?: string[]
    active_org_master_entity_id_count?: number
    active_org_master_entity_id_view?: DrugCardOrgView[]
    originator_org_master_entity_id?: string[]
    originator_org_master_entity_id_count?: number
    originator_org_master_entity_id_view?: DrugCardOrgView[]
    research_disease?: string[]
    research_disease_count?: number
    research_disease_view?: DrugCardDiseaseView[]
    non_research_disease?: string[]
    non_research_disease_count?: number
    non_research_disease_view?: DrugCardDiseaseView[]
    global_highest_dev_status?: string
    global_highest_dev_status_view?: DevStatusView
    data_source?: number
    patent_mining?: boolean
}

export interface OrganizationCardData {
    entity_id: string
    display_name_cn: string
    display_name_en: string
    name_en: string
    name_cn?: string
    name_cn_translation?: string
    other_names_en?: string[]
    normalized_entity_type_en?: string
    entity_status: string
    long_description_en?: string
    short_description_en?: string
    logo?: string
    founded_date?: number
    is_ls_related?: boolean
    country_id?: string
    country_id_view?: CountryView
    employee_number?: number
    ultimate_parent_id?: string[]
    start_up?: boolean
    is_china?: boolean
}

export interface ClinicalTrialPhaseAlias {
    name: string
    lang: string
}

export interface ClinicalTrialPhaseView extends DevStatusView {
    parent_id: string[]
    alias: ClinicalTrialPhaseAlias[]
    ancestor_id: string[]
    is_leaf: boolean
}

export interface ClinicalTrialRecruitmentStatus {
    name: string
    normalized_status: string
}

export interface ClinicalTrialPhase {
    name: string
    normalized_phase: string
    normalized_phase_id: string
    normalized_phase_view: ClinicalTrialPhaseView
    normalized_phase_highest: string
    highest_normalized_phase_id: string
    normalized_phase_highest_view: ClinicalTrialPhaseView
}

export interface ClinicalTrialCardData {
    clinical_trial_id: string
    register_number: string
    url: string
    recruitment_status: ClinicalTrialRecruitmentStatus
    phase: ClinicalTrialPhase
    brief_title: string
    official_title: string
    sponsor_org_master_entity_id: string[]
    sponsor_org_master_entity_id_view: DrugCardOrgView[]
    brief_summary: string
}

export interface ClinicalResultDictView {
    dict_id: string
    dict_type: string
    parent_id?: string[]
    display_name_cn: string
    display_name_en: string
    name_cn?: string
    name_en?: string
    alias?: ClinicalTrialPhaseAlias[]
    ancestor_id?: string[]
    is_leaf?: boolean
}

export interface ClinicalResultSourceInfo {
    source: string
    in_use: number
    for_primary_key: number
    normalized_source_id: string
    normalized_source_id_view: ClinicalResultDictView
    code: string
    source_url: string
}

export interface ClinicalResultTranslation {
    ct_result_id: string
    lang: string
    official_title: string
    conclusion: string
}

export interface ClinicalResultsCardData {
    ct_result_id: string
    official_title: string
    source_info: ClinicalResultSourceInfo[]
    published_time: { time_ts: number; time_trust_code: number }
    general_evaluation_id: string
    general_evaluation_id_view: ClinicalResultDictView
    annotation_method: string
    annotation_method_view: ClinicalResultDictView
    register_number: string[]
    register_number_map: Record<string, string>
    phase_id: string
    phase_id_view: ClinicalTrialPhaseView
    org_master_entity_id: string[]
    org_master_entity_id_view: DrugCardOrgView[]
    priority_org_master_entity_id: string[]
    priority_org_master_entity_id_view: DrugCardOrgView[]
    clinical_trial_translation: ClinicalResultTranslation[]
}

export interface DrugDealDictView {
    dict_id: string
    dict_type: string
    display_name_cn: string
    display_name_en: string
    name_cn?: string
    name_en?: string
}

export interface DrugDealDataSource {
    source: string
    source_url: string
    normalized_source_id: string
    status: string
    in_use: boolean
    patsnap_id?: string
    patsnap_type?: string
}

export interface DrugDealOrgRef {
    name: string
    normalized_id: string
}

export interface DrugDealProjectDrugView {
    drug_id: string
    display_name_cn: string
    display_name_en: string
    drug_name: EntityNameInfo[]
    drug_type?: string[]
    mechanism_action_id?: string[]
    disease_id?: string[]
    target_id?: string[]
    therapeutic_area?: string[]
    data_source?: number
    patent_mining?: boolean
    target_id_count?: number
    mechanism_action_id_count?: number
}

export interface DrugDealProjectDrug {
    name: string
    type: string
    normalized_type: string
    drug_id: string
    drug_id_view?: DrugDealProjectDrugView
    deal_phase?: string
    deal_phase_view?: DevStatusView
}

export interface DrugDealProject {
    name: string
    normalized_id: string
    type: string
    normalized_type: string
    phase_sign?: string
    normalized_phase_sign?: string
    normalized_phase_sign_id?: string
}

export interface DrugDealTitleTranslation {
    deal_id: string
    lang: string
    deal_title: string
}

export interface DrugDealCardData {
    deal_id: string
    deal_title: string
    lang?: string
    data_source?: DrugDealDataSource[]
    deal_start_time?: { time_ts: number; time_trust_code: number }
    org_id?: string[]
    principle_org_id?: string[]
    principle_org_id_view?: DrugCardOrgView[]
    partner_org_id?: string[]
    partner_org_id_view?: DrugCardOrgView[]
    principle_org?: DrugDealOrgRef[]
    partner_org?: DrugDealOrgRef[]
    deal_type?: string[]
    deal_type_view?: DrugDealDictView[]
    cross_border?: string
    cross_border_view?: DrugDealDictView
    deal_project_drug_id?: string[]
    deal_project_drug?: DrugDealProjectDrug[]
    deal_project?: DrugDealProject[]
    drug_indication_id?: string[]
    drug_indication_id_view?: DrugCardDiseaseView[]
    amount_disclosure?: boolean
    deal_status?: string
    deal_status_view?: DrugDealDictView
    data_status?: string
    normalized_phase_sign_id?: string
    target_id?: string[]
    target_id_view?: TargetCardData[]
    drug_type?: string[]
    drug_type_view?: DrugDealDictView[]
    therapeutic_area?: string[]
    therapeutic_area_view?: DrugCardDiseaseView[]
    deal_title_translation?: DrugDealTitleTranslation[]
    principle_head_country?: string[]
    principle_head_country_outside?: string[]
    partner_head_country?: string[]
    partner_head_country_outside?: string[]
}

export interface PaperTitle {
    lang: string
    title: string
}

export interface PaperJournalPartition {
    name: string
    year: number
    zone: string
}

export interface PaperJournalImpactFactor {
    year: number
    value: number
}

export interface PaperJournal {
    abbr_name: string
    journal_partition: PaperJournalPartition[]
    journal_impact_factor: PaperJournalImpactFactor[]
    is_open_access: string
    name: string
    nlm_display_name: string
    normalized_id: string
}

export interface PaperAuthor {
    original_name: string
    ordinal: number
    superscript?: number[]
}

export interface PaperInstitution {
    original_name: string
    normalized_master_entity_id?: string
    ordinal: number
}

export interface PaperPdf {
    source: string
    source_url: string
}

export interface PaperBiblio {
    epub_date?: string
    title_lang?: string[]
    abstract_lang?: string[]
    year?: number
    journal?: PaperJournal
    publisher?: { name: string }
    author?: PaperAuthor[]
    institution?: PaperInstitution[]
    pdf?: PaperPdf[]
}

export interface PaperAbstractSection {
    label: string
    text: string
}

export interface PaperAbstract {
    abst?: PaperAbstractSection[]
    abstract_str?: string
    lang: string
}

export interface PaperCardData {
    paper_id: string
    title: PaperTitle[]
    biblio?: PaperBiblio
    abstract?: PaperAbstract[]
    pubmed_pubtype?: string[]
    article_type?: string[]
    related_pub_types?: string[]
    cited_count?: number
    document_type?: number
    pubmed_id?: number[]
    doi?: string
    source?: string[]
}

export interface EntityApi {
    fetchNerNor(request: NerNorRequest): Promise<NerNorResponse[]>;
    fetchReference(params: { reference_ids: string[] }): Promise<{ data: PDFOrWebsiteReferenceData[] }>;
    fetchDrugCard(id: string, isLogin: boolean): Promise<DrugCardData>;
    fetchTargetCard(id: string, isLogin: boolean): Promise<TargetCardData>;
    fetchOrganizationCard(id: string, isLogin: boolean): Promise<OrganizationCardData>;
    fetchDiseaseCard(id: string, isLogin: boolean): Promise<DiseaseCardData>;
    fetchNewsCard(id: string, isLogin: boolean): Promise<any>;
    fetchPatentCard(id: string, isLogin: boolean): Promise<any>;
    fetchPaperCard(id: string, isLogin: boolean): Promise<PaperCardData>;
    fetchClinicalTrialCard(id: string, isLogin: boolean): Promise<ClinicalTrialCardData>;
    fetchClinicalResultsCard(id: string, isLogin: boolean): Promise<ClinicalResultsCardData>;
    fetchDrugDealCard(id: string, isLogin: boolean): Promise<DrugDealCardData>;
}

export const EntityApi = Symbol('EntityApi');

@injectable()
export class EntityApiImpl extends PsHttpService implements EntityApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0`,
            baseURLGuest: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0/guest`,
            lsBaseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/synapse-api/pharmsnap_webapi/1.0`,
            lsURL: `${import.meta.env.VITE_SYNAPSE_URI}/api/ai-service`,
            headers: {
                'X-Internal-Request': 'true',
            },
        };
    }

    async fetchNerNor(request: NerNorRequest): Promise<NerNorResponse[]> {
        return this.post<PatSnapCommonResponseData<NerNorResponse[]>>({
            url: `${this.options.baseURL}/ner-nor`,
            data: request,
        }).then((res) => {
            return res as unknown as NerNorResponse[];
        });
    }

    async fetchReference(params: { reference_ids: string[] }): Promise<{ data: PDFOrWebsiteReferenceData[] }> {
        return this.post<PatSnapCommonResponseData<{ data: PDFOrWebsiteReferenceData[] }>>({
            url: `${this.options.lsURL}/pia/reference`,
            data: params,
        }).then((res) => {
            return res as unknown as { data: PDFOrWebsiteReferenceData[] };
        });
    }

    private getCardBaseURL(isLogin: boolean): string {
        return isLogin ? this.options.lsBaseURL : this.options.baseURLGuest;
    }

    async fetchDrugCard(id: string, isLogin: boolean): Promise<DrugCardData> {
        return this.get<PatSnapCommonResponseData<DrugCardData>>({ url: `${this.getCardBaseURL(isLogin)}/drug/card/${id}` }).then((res) => {
            return res as unknown as DrugCardData;
        });
    }

    async fetchTargetCard(id: string, isLogin: boolean): Promise<TargetCardData> {
        return this.get<PatSnapCommonResponseData<TargetCardData>>({ url: `${this.getCardBaseURL(isLogin)}/target/card/${id}` }).then((res) => {
            return res as unknown as TargetCardData;
        });
    }

    async fetchOrganizationCard(id: string, isLogin: boolean): Promise<OrganizationCardData> {
        return this.get<PatSnapCommonResponseData<OrganizationCardData>>({ url: `${this.getCardBaseURL(isLogin)}/organization/card/${id}` }).then((res) => {
            return res as unknown as OrganizationCardData;
        });
    }

    async fetchDiseaseCard(id: string, isLogin: boolean): Promise<DiseaseCardData> {
        return this.get<PatSnapCommonResponseData<DiseaseCardData>>({ url: `${this.getCardBaseURL(isLogin)}/disease/card/${id}` }).then((res) => {
            return res as unknown as DiseaseCardData;
        });
    }

    async fetchNewsCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/news/card/${id}` });
    }

    async fetchPatentCard(id: string, isLogin: boolean): Promise<any> {
        return this.get({ url: `${this.getCardBaseURL(isLogin)}/patent/card/${id}` });
    }

    async fetchPaperCard(id: string, isLogin: boolean): Promise<PaperCardData> {
        return this.get<PatSnapCommonResponseData<PaperCardData>>({ url: `${this.getCardBaseURL(isLogin)}/paper/card/${id}` }).then((res) => {
            return res as unknown as PaperCardData;
        });
    }

    async fetchClinicalTrialCard(id: string, isLogin: boolean): Promise<ClinicalTrialCardData> {
        return this.get<PatSnapCommonResponseData<ClinicalTrialCardData>>({ url: `${this.getCardBaseURL(isLogin)}/${isLogin ? 'clinical-trial' : 'clinical_trial'}/card/${id}` }).then((res) => {
            return res as unknown as ClinicalTrialCardData;
        });
    }

    async fetchClinicalResultsCard(id: string, isLogin: boolean): Promise<ClinicalResultsCardData> {
        return this.get<PatSnapCommonResponseData<ClinicalResultsCardData>>({ url: `${this.getCardBaseURL(isLogin)}/ct_result/card/${id}` }).then((res) => {
            return res as unknown as ClinicalResultsCardData;
        });
    }

    async fetchDrugDealCard(id: string, isLogin: boolean): Promise<DrugDealCardData> {
        return this.get<PatSnapCommonResponseData<DrugDealCardData>>({ url: `${this.getCardBaseURL(isLogin)}/drug_deal/card/${id}` }).then((res) => {
            return res as unknown as DrugDealCardData;
        });
    }
}
